import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Pencil, Eye, Save, Loader, Check, Shield } from 'lucide-react';
import { EditModeProvider } from '../context/EditModeContext';
import * as api from '../api/client';
import { useAuth } from '../auth/AuthContext';
import AdminPageContext from './AdminPageContext';

const ADMIN_PAGES = [
  { slug: 'home', label: 'Home', publicPath: '/' },
  { slug: 'about', label: 'About', publicPath: '/about' },
  { slug: 'academics', label: 'Academics', publicPath: '/academics' },
  { slug: 'admissions', label: 'Admissions', publicPath: '/admissions' },
  { slug: 'community', label: 'Community', publicPath: '/community' },
  { slug: 'principal-desk', label: "Principal's Desk", publicPath: '/principal-desk' },
  { slug: 'prospectus', label: 'Prospectus', publicPath: '/prospectus' },
  { slug: 'scottian-life', label: 'Scottian Life', publicPath: '/scottian-life' },
  { slug: 'sports-co-curricular', label: 'Sports', publicPath: '/sports-co-curricular' },
  { slug: 'parent-login', label: 'Parent Login', publicPath: '/parent-login' },
];

/**
 * AdminLayout
 * --------------------------------------------------------------------
 * Sticky toolbar + secondary nav + EditModeProvider wrapper.
 *
 * The toolbar's Save button collects all dirty block changes for the
 * active page slug, merges them into the full block list, and PUTs the
 * whole page via api.updatePage(slug, ...).
 */
export default function AdminLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [editMode, setEditMode] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [saveError, setSaveError] = useState(null);

  // Per-page dirty-block buffer keyed by slug.
  // { [slug]: { [uid]: data } }
  const dirtyRef = useRef({});
  const [dirtyCount, setDirtyCount] = useState(0);

  // The currently mounted <AdminPage /> registers its descriptor here.
  // { slug, title, metaDescription, blocks, setBlocks }
  const currentPageRef = useRef(null);

  const recomputeDirtyCount = useCallback(() => {
    const buf = dirtyRef.current;
    let total = 0;
    Object.keys(buf).forEach((slug) => {
      total += Object.keys(buf[slug] || {}).length;
    });
    setDirtyCount(total);
  }, []);

  const registerPage = useCallback((descriptor) => {
    currentPageRef.current = descriptor;
    return () => {
      if (currentPageRef.current === descriptor) {
        currentPageRef.current = null;
      }
    };
  }, []);

  const applyBlockChange = useCallback(
    (uid, data) => {
      const cur = currentPageRef.current;
      if (!cur) return;
      const slug = cur.slug;
      if (!dirtyRef.current[slug]) dirtyRef.current[slug] = {};
      dirtyRef.current[slug][uid] = data;
      // Also reflect in live block state so the editor UI updates.
      if (cur.setBlocks) {
        cur.setBlocks((prev) =>
          (prev || []).map((b) =>
            b && b.uid === uid ? { ...b, data: { ...(b.data || {}), ...data } } : b
          )
        );
      }
      recomputeDirtyCount();
    },
    [recomputeDirtyCount]
  );

  // EditModeProvider.save(uid, data): just buffer; toolbar Save does the PUT.
  const editSave = useCallback(
    async (uid, data) => {
      applyBlockChange(uid, data);
      return { buffered: true };
    },
    [applyBlockChange]
  );

  const editUpload = useCallback(async (file) => {
    const res = await api.upload(file);
    return { url: res && res.url ? res.url : '', filename: res && res.filename };
  }, []);

  const handleToolbarSave = useCallback(async () => {
    const cur = currentPageRef.current;
    if (!cur) return;
    const slug = cur.slug;
    const pendingForSlug = dirtyRef.current[slug] || {};
    const pendingUids = Object.keys(pendingForSlug);
    if (pendingUids.length === 0) return;

    setSaving(true);
    setSaveError(null);
    try {
      const mergedBlocks = (cur.blocks || []).map((b) =>
        b && pendingForSlug[b.uid]
          ? { ...b, data: { ...(b.data || {}), ...pendingForSlug[b.uid] } }
          : b
      );
      await api.updatePage(slug, {
        title: cur.title,
        meta_description: cur.metaDescription,
        blocks: mergedBlocks,
      });
      // Commit: clear dirty for this slug, update page descriptor's blocks.
      delete dirtyRef.current[slug];
      if (cur.setBlocks) cur.setBlocks(mergedBlocks);
      currentPageRef.current = { ...cur, blocks: mergedBlocks };
      setLastSavedAt(new Date());
      recomputeDirtyCount();
    } catch (err) {
      setSaveError(err && err.message ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }, [recomputeDirtyCount]);

  const handlePreview = useCallback(() => {
    const cur = currentPageRef.current;
    const page = ADMIN_PAGES.find((p) => p.slug === (cur && cur.slug));
    const path = page ? page.publicPath : '/';
    window.open(path, '_blank', 'noopener');
  }, []);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/website_admin/login', { replace: true });
  }, [logout, navigate]);

  // Redirect /website_admin → /website_admin/home.
  useEffect(() => {
    if (location.pathname === '/website_admin' || location.pathname === '/website_admin/') {
      navigate('/website_admin/home', { replace: true });
    }
  }, [location.pathname, navigate]);

  const bridgeValue = useMemo(
    () => ({
      registerPage,
      applyBlockChange,
      getCurrentPage: () => currentPageRef.current,
    }),
    [registerPage, applyBlockChange]
  );

  let saveStatus;
  if (saving) {
    saveStatus = (
      <span className="inline-flex items-center gap-1.5 text-amber-600 text-xs">
        <Loader size={12} className="animate-spin" />
        Saving…
      </span>
    );
  } else if (dirtyCount > 0) {
    saveStatus = (
      <span className="text-xs text-amber-700">
        {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
      </span>
    );
  } else if (lastSavedAt) {
    saveStatus = (
      <span className="inline-flex items-center gap-1.5 text-emerald-600 text-xs">
        <Check size={12} />
        Saved
      </span>
    );
  } else {
    saveStatus = <span className="text-xs text-gray-400">All changes saved</span>;
  }

  const activeSlug = (() => {
    const m = location.pathname.match(/\/website_admin\/([^/]+)/);
    return m ? m[1] : 'home';
  })();

  const handlePageChange = useCallback(
    (e) => {
      const next = e.target.value;
      if (next === '_seed') navigate('/website_admin/_seed');
      else navigate(`/website_admin/${next}`);
    },
    [navigate]
  );

  return (
    <EditModeProvider
      isAdmin={true}
      initialEditMode={true}
      save={editSave}
      upload={editUpload}
    >
      <AdminPageContext.Provider value={bridgeValue}>
        <div className="min-h-screen bg-white">
          {/* Floating compact toolbar — does not visually disturb the page layout */}
          <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[100] bg-white/95 backdrop-blur border border-gray-200 shadow-lg rounded-full pl-2 pr-2 py-1 flex items-center gap-2 text-sm">
            <Link
              to="/website_admin/home"
              className="inline-flex items-center gap-1.5 px-2 py-1 font-semibold text-gray-900"
              title="Website Admin"
            >
              <Shield size={14} className="text-indigo-700" />
              <span className="hidden sm:inline">Admin</span>
            </Link>

            <span className="w-px h-5 bg-gray-200" />

            <select
              value={activeSlug}
              onChange={handlePageChange}
              className="text-xs bg-transparent border-0 focus:ring-0 focus:outline-none cursor-pointer pr-6 py-1"
              title="Switch page"
            >
              {ADMIN_PAGES.map((p) => (
                <option key={p.slug} value={p.slug}>{p.label}</option>
              ))}
              <option value="_seed">— Seed loader —</option>
            </select>

            <span className="w-px h-5 bg-gray-200" />

            <span className="hidden md:inline-flex items-center px-1">{saveStatus}</span>

            <button
              type="button"
              onClick={() => setEditMode((v) => !v)}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                editMode
                  ? 'bg-indigo-700 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title={editMode ? 'Edit mode ON' : 'Edit mode OFF'}
            >
              <Pencil size={12} />
              {editMode ? 'Editing' : 'Edit'}
            </button>

            <button
              type="button"
              onClick={handlePreview}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs text-gray-600 hover:bg-gray-100"
              title="Open public page in new tab"
            >
              <Eye size={12} />
            </button>

            <button
              type="button"
              onClick={handleToolbarSave}
              disabled={saving || dirtyCount === 0}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Save changes"
            >
              {saving ? <Loader size={12} className="animate-spin" /> : <Save size={12} />}
              Save
            </button>

            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-500 hover:bg-gray-100"
              title="Logout"
            >
              <LogOut size={12} />
            </button>
          </div>

          {saveError && (
            <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[99] bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-1 rounded-md shadow">
              {saveError}
            </div>
          )}

          <Outlet />
        </div>
      </AdminPageContext.Provider>
    </EditModeProvider>
  );
}
